import React, { CSSProperties } from "react";

import { InputAdornment, MenuItem } from "@mui/material";
import type { SelectProps } from "@mui/material";
import { useController } from "react-hook-form";
import { cvar } from "styles";
import { useSmartFormContext } from "components/SmartForm";
import { Tooltip } from "components/mui-styled-components/Tooltip";
import { $TSFixMe } from "types";
import { TooltipIcon, TooltipText } from "@mui-styled-components/SmartFormElements/SmartFormTextField/styles";
import HelperText from "../HelperText";

import * as S from "./styles";

type SmartFormDropdownT = {
  label: string | React.ReactNode;
  value: $TSFixMe;
  onClick?: undefined | (() => void);
  disabled?: boolean;
  hidden?: boolean;
  tooltip?: string;
}[];

export type SmartFormDropdownProps = {
  mainLabel?: string;
  dropdownOptions: SmartFormDropdownT;
  name: string;
  helperText?: string | undefined;
  error?: boolean;
  disabled?: boolean;
  width?: string;
  mainLabelStyle?: CSSProperties;
  menuItemStyle?: CSSProperties;
  helperTextStyle?: CSSProperties;
  required?: boolean;
  fullWidth?: boolean;
  prefixIcon?: React.ReactNode;
  prefixIconOnClick?: () => void;
  placeholder?: string;
  hasRedHighlight?: boolean;
  tooltipText?: string;
  fieldHoverTooltipText?: string | null;
} & SelectProps;

const DropdownIcon = () => (
  <S.IconContainer>
    <S.StyledDownCaretFilled />
  </S.IconContainer>
);

const SmartFormDropdown = ({
  mainLabel,
  dropdownOptions,
  name,
  helperText,
  error,
  disabled = false,
  mainLabelStyle = {},
  menuItemStyle = {},
  helperTextStyle = {},
  width = "100%",
  required,
  fullWidth = true,
  prefixIcon,
  prefixIconOnClick,
  placeholder,
  hasRedHighlight = false,
  tooltipText,
  fieldHoverTooltipText = null,
  sx,
  ...rest
}: SmartFormDropdownProps) => {
  const { controllerProps } = useSmartFormContext();
  const { field } = useController({ ...controllerProps, name });

  return (
    <>
      <S.LabelGroupWrapper>
        <S.StyledFormLabel style={mainLabelStyle}>{mainLabel}</S.StyledFormLabel>
        {required && <S.StyledRequiredLabel>*</S.StyledRequiredLabel>}
        {tooltipText && (
          <Tooltip placement="top" title={<TooltipText>{tooltipText}</TooltipText>}>
            <TooltipIcon>!</TooltipIcon>
          </Tooltip>
        )}
      </S.LabelGroupWrapper>
      <Tooltip placement="top" title={fieldHoverTooltipText}>
        <S.StyledDropdown
          sx={{
            background: disabled ? cvar("botticelli-blue-50") : cvar("white"),
            height: "32px",
            width,
            ...(prefixIcon ? { paddingLeft: "8px" } : {}),
            ...sx,
          }}
          fullWidth={fullWidth}
          disabled={disabled}
          displayEmpty
          {...field}
          MenuProps={{
            MenuListProps: {
              dense: true,
              sx: { color: cvar("dark-blue"), fontSize: "16px" },
            },
          }}
          {...(prefixIcon
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <S.PrefixContainer onClick={prefixIconOnClick}>{prefixIcon}</S.PrefixContainer>
                  </InputAdornment>
                ),
              }
            : {})}
          IconComponent={DropdownIcon}
          defaultValue={rest.defaultValue || ""}
          redHighlight={hasRedHighlight}
          data-test-id={`smart-form-dropdown-${name}`}
          {...rest}
        >
          {placeholder && (
            <MenuItem value="" disabled selected hidden>
              <S.Placeholder>{placeholder}</S.Placeholder>
            </MenuItem>
          )}
          {dropdownOptions.map((opt) => {
            const menuItem = (
              <MenuItem
                value={opt.value}
                key={String(opt.value ?? opt.label)}
                onClick={opt?.onClick}
                disabled={opt?.disabled}
                hidden={opt?.hidden}
                style={{ ...menuItemStyle, cursor: opt?.disabled ? "not-allowed" : "pointer" }}
              >
                {opt.label}
              </MenuItem>
            );

            return opt.tooltip ? (
              <Tooltip placement="left" title={opt.tooltip}>
                <div>{menuItem}</div>
              </Tooltip>
            ) : (
              menuItem
            );
          })}
        </S.StyledDropdown>
      </Tooltip>
      <HelperText sx={{ marginTop: "-10px", ...helperTextStyle }} error={error}>
        {helperText}
      </HelperText>
    </>
  );
};

export type { SmartFormDropdownT };

export default SmartFormDropdown;
